import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';
import { useFeedingSchedule } from '@/hooks/useFeedingSchedule';
import { api } from '@/lib/api';
import { saveFailureMessage } from '@/lib/apiErrors';
import { cn } from '@/lib/cn';
import { qk } from '@/lib/queryKeys';
import type { FeedingRange, FeedingSchedule } from '@/lib/feedingSchedule';

// Chronological, as the API sorts them.
const ALL_SLOTS = ['7am', '10am', '11am', '1pm', '3pm', '4pm', '7pm'];

interface DraftRange {
  from: string;
  to: string;
  slots: string[];
}

const toDraft = (r: FeedingRange): DraftRange => ({ from: String(r.from_doc), to: r.to_doc === null ? '' : String(r.to_doc), slots: r.slots });

function rangeError(r: DraftRange): string | null {
  const from = Number(r.from);
  const to = r.to.trim() === '' ? null : Number(r.to);
  if (!Number.isInteger(from) || from < 1) return 'Start day must be 1 or more';
  if (to !== null && (!Number.isInteger(to) || to < from)) return 'End day must be the start day or later';
  if (r.slots.length === 0) return 'Pick at least one feeding time';
  return null;
}

function overlapError(ranges: DraftRange[]): string | null {
  const parsed = ranges.map((r) => ({ from: Number(r.from), to: r.to.trim() === '' ? Infinity : Number(r.to) })).sort((a, b) => a.from - b.from);
  for (let i = 0; i + 1 < parsed.length; i++) if (parsed[i].to >= parsed[i + 1].from) return 'Day ranges must not overlap';
  return null;
}

export function FeedingScheduleEditor({ farmId, canEdit }: { farmId: string; canEdit: boolean }) {
  const { data, isLoading, isError } = useFeedingSchedule(farmId);
  if (isLoading) {
    return (
      <Card>
        <Card.Body>
          <LoadingSpinner />
        </Card.Body>
      </Card>
    );
  }
  if (isError || !data) return null;
  return <Editor key={JSON.stringify(data)} farmId={farmId} schedule={data} canEdit={canEdit} />;
}

function Editor({ farmId, schedule, canEdit }: { farmId: string; schedule: FeedingSchedule; canEdit: boolean }) {
  const queryClient = useQueryClient();
  const [enabled, setEnabled] = useState(schedule.enabled);
  // The server always spells the ranges out (the farm's own, or those derived from its times).
  const [ranges, setRanges] = useState<DraftRange[]>(() => schedule.ranges.map(toDraft));
  const [touched, setTouched] = useState(false);

  const errors = ranges.map(rangeError);
  const overlap = errors.some(Boolean) ? null : overlapError(ranges);
  // No ranges at all would silently become the derived defaults: say so instead of saving it.
  const noRanges = enabled && ranges.length === 0;
  const dirty = enabled !== schedule.enabled || (enabled && touched);

  const save = useMutation({
    mutationFn: () => {
      const feeding_schedule: Record<string, unknown> = { enabled };
      // The save replaces the whole key. Ranges the farm never set stay derived from its times, and
      // switching off never sends a half-edited draft: it keeps exactly what the server holds.
      const edited = enabled && touched;
      const source = edited ? ranges.map((r) => ({ from_doc: Number(r.from), to_doc: r.to.trim() === '' ? null : Number(r.to), slots: r.slots })) : schedule.ranges;
      if (edited || schedule.custom_ranges) feeding_schedule.ranges = source;
      return api.put(`/api/v1/farms/${farmId}/settings`, { feeding_schedule });
    },
    // The editor re-mounts on the new server value, so a toast carries the confirmation.
    onSuccess: () => {
      toast.success('Feeding schedule saved');
      queryClient.invalidateQueries({ queryKey: qk.feedingSchedule.farm(farmId) });
    },
  });

  const edit = (i: number, patch: Partial<DraftRange>) => {
    setTouched(true);
    setRanges((rs) => rs.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  };
  const toggleSlot = (i: number, slot: string) =>
    edit(i, { slots: ranges[i].slots.includes(slot) ? ranges[i].slots.filter((s) => s !== slot) : [...ranges[i].slots, slot] });

  return (
    <Card>
      <Card.Header>
        <Card.Title>Feeding times by day of culture</Card.Title>
      </Card.Header>
      <Card.Body>
        <p className="mb-3 text-sm text-gray-500">
          Off: every day uses the farm's feeding times ({schedule.default_slots.join(', ')}). On: the feeding times a pond
          shows depend on its day of culture, e.g. fewer feedings on the day the shrimp arrive.
          {!canEdit && ' Only a Farm Owner can change this.'}
        </p>
        <label className="mb-3 flex min-h-[44px] items-center gap-2 text-sm font-medium text-gray-900">
          <input
            type="checkbox"
            checked={enabled}
            disabled={!canEdit}
            onChange={(e) => {
              setEnabled(e.target.checked);
              // Turning it on for a farm with no schedule of its own starts from the preset (day 1 = 3pm and 7pm),
              // not from the first slots of the farm's list; every range stays editable before Save.
              if (e.target.checked && !touched && !schedule.custom_ranges && schedule.starting_preset?.length) {
                setRanges(schedule.starting_preset.map(toDraft));
                setTouched(true);
              }
            }}
            className="h-5 w-5"
          />
          Use a different schedule by day of culture
        </label>
        {canEdit && schedule.starting_preset && schedule.starting_preset.length > 0 && (
          <div className="mb-3">
            <button
              type="button"
              data-feeding-preset
              onClick={() => {
                setEnabled(true);
                setTouched(true);
                setRanges(schedule.starting_preset!.map(toDraft));
              }}
              className="min-h-[44px] rounded-lg border border-gray-300 bg-white px-4 text-sm font-semibold text-gray-700"
            >
              Start from: 2 feedings on day 1, 4 a day to day 30, then 5 a day
            </button>
            <p className="mt-1 text-xs text-gray-500">
              Fills the ranges below; nothing is saved until you press Save. Day 1 is 3pm and 7pm; change any
              range to fit the farm.
            </p>
          </div>
        )}
        {enabled && (
          <div className="space-y-3" data-feeding-ranges>
            {ranges.map((r, i) => (
              <div key={i} className="rounded-lg border border-gray-200 p-3" data-feeding-range>
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className="text-gray-600">From day</span>
                  <input
                    inputMode="numeric"
                    value={r.from}
                    disabled={!canEdit}
                    onChange={(e) => edit(i, { from: e.target.value })}
                    aria-label={`Range ${i + 1} start day`}
                    className="min-h-[36px] w-16 rounded-md border border-gray-300 px-2 tabular-nums"
                  />
                  <span className="text-gray-600">to</span>
                  <input
                    inputMode="numeric"
                    value={r.to}
                    placeholder="onward"
                    disabled={!canEdit}
                    onChange={(e) => edit(i, { to: e.target.value })}
                    aria-label={`Range ${i + 1} end day`}
                    className="min-h-[36px] w-20 rounded-md border border-gray-300 px-2 tabular-nums"
                  />
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => {
                        setTouched(true);
                        setRanges((rs) => rs.filter((_, j) => j !== i));
                      }}
                      className="ml-auto text-xs font-semibold text-red-700"
                    >
                      Remove
                    </button>
                  )}
                </div>
                <div className="mt-2 flex flex-wrap gap-1.5" role="group" aria-label={`Range ${i + 1} feeding times`}>
                  {ALL_SLOTS.map((slot) => (
                    <button
                      key={slot}
                      type="button"
                      aria-pressed={r.slots.includes(slot)}
                      disabled={!canEdit}
                      onClick={() => toggleSlot(i, slot)}
                      className={cn(
                        'min-h-[36px] min-w-[52px] rounded-full border px-3 text-sm font-medium',
                        r.slots.includes(slot) ? 'border-primary-600 bg-primary-600 text-white' : 'border-gray-300 bg-white text-gray-700'
                      )}
                    >
                      {slot}
                    </button>
                  ))}
                </div>
                {errors[i] && (
                  <p role="alert" className="mt-1.5 text-xs text-red-600">
                    {errors[i]}
                  </p>
                )}
              </div>
            ))}
            {noRanges && (
              <p role="alert" className="text-xs text-red-600">
                Add at least one range, or switch the schedule off.
              </p>
            )}
            {overlap && (
              <p role="alert" className="text-xs text-red-600">
                {overlap}
              </p>
            )}
            {canEdit && ranges.length < 6 && (
              <button
                type="button"
                onClick={() => {
                  setTouched(true);
                  const last = ranges[ranges.length - 1];
                  const next = last && last.to.trim() !== '' ? Number(last.to) + 1 : 1;
                  setRanges((rs) => [...rs, { from: String(next), to: '', slots: schedule.default_slots }]);
                }}
                className="text-sm font-semibold text-primary-600"
              >
                + Add a range
              </button>
            )}
            <p className="text-xs text-gray-500">A day outside every range uses the farm's feeding times.</p>
          </div>
        )}
        {canEdit && (
          <div className="mt-4 flex items-center justify-end gap-3">
            {save.isError && (
              <span role="alert" className="text-sm text-red-600">
                Not saved: {saveFailureMessage(save.error, 'try again')}
              </span>
            )}
            <Button
              onClick={() => save.mutate()}
              disabled={!dirty || (enabled && (errors.some(Boolean) || !!overlap || noRanges)) || save.isPending}
              loading={save.isPending}
              className="min-h-[44px]"
            >
              Save feeding schedule
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
}
